// Location: src/components/shared/HistoricalTradesDialog.tsx
// Purpose: Historical trades dialog for viewing historical trades
// Scope: Shows historical trades in a searchable and filterable interface

import * as React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader } from '@/components/ui/loader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button';
import { Doc } from '@/convex/_generated/dataModel';

interface HistoricalTradesDialogProps {
	trigger: React.ReactNode;
	initialYear?: number;
}

const YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022];

// Define the type for a single trade object
type HistoricalTrade = Doc<'historicalTrades'>;

export function HistoricalTradesDialog({
	trigger,
	initialYear = 2016,
}: HistoricalTradesDialogProps) {
	const [selectedYear, setSelectedYear] = React.useState(initialYear);
	const trades = useQuery(api.oracle.getHistoricalTrades, {
		year: selectedYear,
	});
	const [selectedTrade, setSelectedTrade] = React.useState<number | null>(
		null
	);

	const totalReturn =
		trades?.reduce(
			(acc: number, trade: HistoricalTrade) =>
				acc + trade.percentageReturn,
			0
		) || 0;
	const winningTrades =
		trades?.filter((trade: HistoricalTrade) => trade.profitLoss > 0)
			.length || 0;
	const totalTrades = trades?.length || 0;
	const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

	// Calculate initial investment and total profit
	const initialInvestment = trades?.[0]?.initialCapital || 0;
	const totalProfit =
		trades?.reduce(
			(acc: number, trade: HistoricalTrade) => acc + trade.profitLoss,
			0
		) || 0;

	const handlePreviousYear = () => {
		const currentIndex = YEARS.indexOf(selectedYear);
		if (currentIndex > 0) {
			setSelectedYear(YEARS[currentIndex - 1]);
		}
	};

	const handleNextYear = () => {
		const currentIndex = YEARS.indexOf(selectedYear);
		if (currentIndex < YEARS.length - 1) {
			setSelectedYear(YEARS[currentIndex + 1]);
		}
	};

	return (
		<Dialog>
			<DialogTrigger asChild>{trigger}</DialogTrigger>
			<DialogContent className='max-w-4xl h-[90vh] flex flex-col overflow-hidden'>
				<DialogHeader className='flex-none'>
					<div className='flex items-center justify-between'>
						<Button
							variant='outline'
							size='sm'
							onClick={handlePreviousYear}
							disabled={selectedYear === YEARS[0]}
							className='mr-2'
						>
							<ChevronLeftIcon className='h-4 w-4' />
						</Button>
						<DialogTitle className='text-3xl font-bold bg-gradient-to-r from-pink-400 to-purple-600 text-transparent bg-clip-text pb-2'>
							Historical Trades {selectedYear}
						</DialogTitle>
						<Button
							variant='outline'
							size='sm'
							onClick={handleNextYear}
							disabled={selectedYear === YEARS[YEARS.length - 1]}
							className='ml-2'
						>
							<ChevronRightIcon className='h-4 w-4' />
						</Button>
					</div>
					<div className='grid grid-cols-5 gap-4 mt-4'>
						<div className='bg-gradient-to-b from-orange-400/20 to-amber-600/20 border border-orange-500/30 rounded-lg p-4 col-span-2'>
							<div className='text-2xl font-bold text-orange-400'>
								${initialInvestment.toLocaleString()}
							</div>
							<div className='text-sm text-gray-400'>
								Initial Investment
							</div>
						</div>
						<div className='bg-gradient-to-b from-emerald-400/20 to-green-600/20 border border-emerald-500/30 rounded-lg p-4 col-span-3'>
							<div className='text-2xl font-bold text-emerald-400'>
								${totalProfit.toLocaleString()} (
								{totalReturn.toFixed(2)}%)
							</div>
							<div className='text-sm text-gray-400'>
								Total Profit & Return
							</div>
						</div>
						<div className='bg-gradient-to-b from-blue-400/20 to-indigo-600/20 border border-blue-500/30 rounded-lg p-4'>
							<div className='text-2xl font-bold text-blue-400'>
								{winningTrades}/{totalTrades}
							</div>
							<div className='text-sm text-gray-400'>
								Winning Trades
							</div>
						</div>
						<div className='bg-gradient-to-b from-purple-400/20 to-pink-600/20 border border-purple-500/30 rounded-lg p-4'>
							<div className='text-2xl font-bold text-purple-400'>
								{winRate.toFixed(1)}%
							</div>
							<div className='text-sm text-gray-400'>
								Win Rate
							</div>
						</div>
						<div className='bg-gradient-to-b from-teal-400/20 to-cyan-600/20 border border-teal-500/30 rounded-lg p-4'>
							<div className='text-2xl font-bold text-teal-400'>
								$
								{trades?.[
									trades.length - 1
								]?.totalCapital.toLocaleString()}
							</div>
							<div className='text-sm text-gray-400'>
								Final Capital
							</div>
						</div>
					</div>
				</DialogHeader>

				<div className='flex-1 overflow-hidden mt-6'>
					<ScrollArea className='h-full'>
						<div className='space-y-4 pr-4'>
							{trades ? (
								<AnimatePresence mode='wait'>
									{trades.map((trade: HistoricalTrade) => (
										<motion.div
											key={`${selectedYear}-${trade.tradeNumber}`}
											initial={{ opacity: 0, y: 20 }}
											animate={{ opacity: 1, y: 0 }}
											exit={{ opacity: 0, y: -20 }}
											transition={{ duration: 0.2 }}
											className='relative'
											onHoverStart={() =>
												setSelectedTrade(
													trade.tradeNumber
												)
											}
											onHoverEnd={() =>
												setSelectedTrade(null)
											}
										>
											<div
												className={cn(
													'p-6 rounded-lg border transition-all duration-200',
													'bg-gradient-to-b backdrop-blur-sm',
													trade.direction === 'LONG'
														? 'from-green-500/20 to-emerald-600/20 border-green-500/30'
														: 'from-red-500/20 to-pink-600/20 border-red-500/30',
													selectedTrade ===
														trade.tradeNumber &&
														'transform scale-[1.02] shadow-xl'
												)}
											>
												<div className='flex justify-between items-start mb-4'>
													<div className='space-y-1'>
														<div className='flex items-center gap-2'>
															<Badge
																variant='outline'
																className={cn(
																	'px-3 py-1',
																	trade.direction ===
																		'LONG'
																		? 'bg-green-500/20 text-green-400 border-green-500/30'
																		: 'bg-red-500/20 text-red-400 border-red-500/30'
																)}
															>
																Trade #
																{
																	trade.tradeNumber
																}
															</Badge>
															<Badge
																variant='outline'
																className={cn(
																	'px-3 py-1',
																	trade.profitLoss >
																		0
																		? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
																		: 'bg-red-500/20 text-red-400 border-red-500/30'
																)}
															>
																{trade.profitLoss >
																0
																	? '📈 Profit'
																	: '📉 Loss'}
															</Badge>
														</div>
														<h3 className='text-2xl font-bold'>
															SPY{' '}
															{trade.optionType}
														</h3>
													</div>
													<div className='text-right'>
														<div className='text-sm text-gray-400'>
															{format(
																new Date(
																	trade.entryDate
																),
																'MMM dd, yyyy'
															)}{' '}
															-{' '}
															{format(
																new Date(
																	trade.exitDate
																),
																'MMM dd, yyyy'
															)}
														</div>
													</div>
												</div>

												<div className='grid grid-cols-3 gap-6 mt-4'>
													<div>
														<div className='text-sm text-gray-400 mb-1'>
															Capital Deployed
														</div>
														<div className='text-lg font-semibold'>
															$
															{trade.capitalDeployed.toLocaleString()}
														</div>
													</div>
													<div>
														<div className='text-sm text-gray-400 mb-1'>
															Profit/Loss
														</div>
														<div
															className={cn(
																'text-lg font-semibold',
																trade.profitLoss >
																	0
																	? 'text-emerald-400'
																	: 'text-red-400'
															)}
														>
															$
															{Math.abs(
																trade.profitLoss
															).toLocaleString()}
														</div>
													</div>
													<div>
														<div className='text-sm text-gray-400 mb-1'>
															Return
														</div>
														<div
															className={cn(
																'text-lg font-semibold',
																trade.percentageReturn >
																	0
																	? 'text-emerald-400'
																	: 'text-red-400'
															)}
														>
															{trade.percentageReturn.toFixed(
																2
															)}
															%
														</div>
													</div>
												</div>

												<div className='mt-4 pt-4 border-t border-gray-700'>
													<div className='text-sm text-gray-400'>
														Portfolio Value: $
														{trade.totalCapital.toLocaleString()}
													</div>
												</div>
											</div>
										</motion.div>
									))}
								</AnimatePresence>
							) : (
								<div className='flex justify-center items-center py-12'>
									<Loader message='LOADING HISTORICAL TRADES...' />
								</div>
							)}
						</div>
					</ScrollArea>
				</div>
			</DialogContent>
		</Dialog>
	);
}
